import React from 'react';
import { Autocomplete, Box, Chip, TextField as MuiTextField, Typography } from '@mui/material';
import { useLocales, useTheme } from '../../../../hooks';
import { makeStyles } from 'tss-react/mui';
import { Control, Controller } from 'react-hook-form';
import FormControl from '../../FormControl';
import FoldableSection from '../../FoldableSection';
import { enrichTestIds } from '../../TestsIds';
import { EnrichForm } from './Enrich';

interface SearchSynonymsProps {
  control: Control<EnrichForm>;
  isSaving: boolean;
}

export const useStyles = makeStyles()((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    margin: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  autocomplete: {
    minWidth: 450,
    flexGrow: 1
  }
}));

// Editors type the same term more than once across a session, and a repeated term adds nothing to the
// search index, so tags are trimmed and deduplicated as they are created
const toTags = (values: string[]): string[] => {
  const tags = values.map((value) => value.trim()).filter(Boolean);
  return tags.filter((tag, index) => tags.indexOf(tag) === index);
};

export function SearchSynonyms({ control, isSaving }: SearchSynonymsProps): JSX.Element {
  const { classes } = useStyles();
  const { formControlColor } = useTheme();
  const { t } = useLocales();

  return (
    <FoldableSection
      summary={<Typography variant="h6">{t('content.drawer.search_synonyms.foldable_title')}</Typography>}
      expanded
      disableGutters
    >
      <Box className={classes.form}>
        <Controller
          control={control}
          name="synonyms"
          render={({ field: { value, onChange } }) => (
            <FormControl>
              <Autocomplete
                multiple
                freeSolo
                // Commits whatever is still in the input when the field loses focus. Without this,
                // typing a keyword and going straight to Update silently discards it: freeSolo only
                // turns text into a tag on Enter.
                autoSelect
                options={[]}
                value={value ?? []}
                onChange={(_, newValue) => onChange(toTags(newValue as string[]))}
                disabled={isSaving}
                className={classes.autocomplete}
                renderTags={(tagValue: readonly string[], getTagProps) =>
                  tagValue.map((option: string, index: number) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={`${index}-${option}`}
                      label={option}
                      data-testid={enrichTestIds.searchSynonymsChip}
                    />
                  ))
                }
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    color={formControlColor}
                    label={t('content.drawer.search_synonyms.keywords')}
                    placeholder={t('content.drawer.search_synonyms.placeholder')}
                    helperText={t('content.drawer.search_synonyms.helper_text')}
                    inputProps={{ ...params.inputProps, role: 'combobox' }}
                    data-testid={enrichTestIds.searchSynonymsInput}
                  />
                )}
              />
            </FormControl>
          )}
        />
      </Box>
    </FoldableSection>
  );
}
